import React from 'react';
import { motion } from 'framer-motion';
import { PIECE_CONSTANTS, getPieceType, isKing } from './logic';
import './Checkers.css';

const { EMPTY, BLACK_PIECE, BLACK_KING, RED_PIECE, RED_KING } = PIECE_CONSTANTS;

const CheckersBoard = ({
  board,
  selectedSquare,
  legalMoves,
  lastMove,
  onSquareClick,
  disabled,
  myColor,
  boardColors = { light: '#f0d9b5', dark: '#b58863' },
}) => {
  // Safety check
  if (!board || !Array.isArray(board) || board.length !== 8) {
    return (
      <div className="checkers-board-container">
        <div className="checkers-board">
          <div style={{ color: 'white', padding: '2rem' }}>Loading board...</div>
        </div>
      </div>
    );
  }

  // Flip board if playing as black (so player's pieces are always at bottom)
  const shouldFlipBoard = myColor === 'black';

  // Create display board (potentially flipped)
  const displayBoard = shouldFlipBoard
    ? [...board].reverse().map(row => [...row].reverse())
    : board;

  // Helper to convert display coordinates to actual board coordinates
  const toActualCoords = (displayRow, displayCol) => {
    if (shouldFlipBoard) {
      return {
        row: 7 - displayRow,
        col: 7 - displayCol
      };
    }
    return { row: displayRow, col: displayCol };
  };

  // Helper to convert actual coordinates to display coordinates
  const toDisplayCoords = (actualRow, actualCol) => {
    if (shouldFlipBoard) {
      return {
        row: 7 - actualRow,
        col: 7 - actualCol
      };
    }
    return { row: actualRow, col: actualCol };
  };

  const isLightSquare = (row, col) => {
    return (row + col) % 2 === 0;
  };

  const isSelected = (actualRow, actualCol) => {
    return selectedSquare && selectedSquare.row === actualRow && selectedSquare.col === actualCol;
  };

  const isLegalMove = (actualRow, actualCol) => {
    return legalMoves && legalMoves.some(move => move.row === actualRow && move.col === actualCol);
  };

  const isLastMoveSquare = (actualRow, actualCol) => {
    if (!lastMove) return false;
    return (
      (lastMove.from.row === actualRow && lastMove.from.col === actualCol) ||
      (lastMove.to.row === actualRow && lastMove.to.col === actualCol)
    );
  };

  const getSquareClasses = (displayRow, displayCol, actualRow, actualCol) => {
    const classes = ['checkers-square'];

    if (isLightSquare(displayRow, displayCol)) {
      classes.push('light');
    } else {
      classes.push('dark');
    }

    if (isSelected(actualRow, actualCol)) {
      classes.push('selected');
    }

    if (isLegalMove(actualRow, actualCol)) {
      classes.push('legal-move');
    }

    if (isLastMoveSquare(actualRow, actualCol)) {
      classes.push('last-move');
    }

    return classes.join(' ');
  };

  const getPieceDisplay = (piece) => {
    if (piece === EMPTY) return null;

    const pieceColor = getPieceType(piece);
    const isPieceKing = isKing(piece);

    return (
      <motion.div
        className={`checker-piece ${pieceColor}${isPieceKing ? ' king' : ''}`}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      >
        {isPieceKing && <span className="crown">👑</span>}
      </motion.div>
    );
  };

  return (
    <div className="checkers-board-container">
      <div
        className="checkers-board"
        style={{
          '--light-square-color': boardColors.light,
          '--dark-square-color': boardColors.dark,
        }}
      >
        {displayBoard.map((row, displayRowIndex) =>
          row.map((piece, displayColIndex) => {
            const { row: actualRow, col: actualCol } = toActualCoords(displayRowIndex, displayColIndex);
            const squareKey = `${displayRowIndex}-${displayColIndex}`;

            return (
              <div
                key={squareKey}
                className={getSquareClasses(displayRowIndex, displayColIndex, actualRow, actualCol)}
                onClick={() => !disabled && onSquareClick(actualRow, actualCol)}
                style={{
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  backgroundColor: isLightSquare(displayRowIndex, displayColIndex)
                    ? boardColors.light
                    : boardColors.dark,
                }}
              >
                {/* Coordinate labels */}
                {displayColIndex === 0 && (
                  <div className="row-label">
                    {shouldFlipBoard ? displayRowIndex + 1 : 8 - displayRowIndex}
                  </div>
                )}
                {displayRowIndex === 7 && (
                  <div className="col-label">
                    {String.fromCharCode(97 + (shouldFlipBoard ? 7 - displayColIndex : displayColIndex))}
                  </div>
                )}

                {/* Legal move indicator */}
                {isLegalMove(actualRow, actualCol) && (
                  <div className="move-indicator"></div>
                )}

                {/* Piece */}
                {piece !== EMPTY && getPieceDisplay(piece)}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CheckersBoard;
